import React, { useEffect, useRef, useState } from "react";
import { Table } from "@timeedit/tecore-table";
import ContextMenu from "../lib/ContextMenu";
import Log from "../lib/Log";
import Language from "../lib/Language";
import _ from "underscore";
import { TimeEdit } from "../lib/TimeEdit";
import API from "../lib/TimeEditAPI";
import getMembershipColumn from "../lib/MembershipConstants";
import { useAppContext } from "../contexts/AppContext";

type TObjectListProps = {
    searchObject?: any;
    updateSearchObject?: any;
    loadFunction?: any;
    objects?: any;
    options?: any;
    presentMembershipPeriod?: any;
    totalNumber?: number | null;
    cssClass?: string;
    flex?: boolean;
    cellRenderers?: any;
    hidePlusButton?: boolean;
    onChange?: any;
    hoverButton?: any;
    allowMultiSelection?: boolean;
    columnTitle?: React.ReactNode;
    listSize?: number;
};

const getTypeName = (typeId, types, rootType) => {
    if (typeId === 0 || typeId === rootType.id) {
        return rootType.name;
    }
    return _.find(types, (type) => type.id === typeId).name;
};

const ObjectList = (props: TObjectListProps) => {
    const appContext = useAppContext();
    const [objects, setObjects] = useState<any[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<any>({});

    const isMounted = useRef(false);

    const load = (forProps: TObjectListProps) => {
        forProps.loadFunction(forProps.objects, (loaded) => {
            if (isMounted.current) {
                setObjects(loaded.filter((obj) => obj !== null));
            }
        });
    };

    useEffect(() => {
        isMounted.current = true;
        load(props);

        API.getPreferences("multiObjectSelectColumns", [], undefined, (result) => {
            if (!result || (result as []).length === 0) {
                return;
            }

            const newSettings = JSON.parse(result[0]);
            setSelectedColumns(newSettings);
            // The class read the freshly committed state in a setState callback;
            // using newSettings directly is the same value without the round trip.
            if (props.searchObject) {
                const type = props.searchObject.type;
                if (
                    newSettings[type] &&
                    !_.isEqual(props.searchObject.defaultColumns, newSettings[type])
                ) {
                    const newSearch = props.searchObject.setDefaultColumns(newSettings[type]);
                    props.updateSearchObject(newSearch);
                }
            }
        });

        return () => {
            isMounted.current = false;
        };
    }, []);

    // componentDidUpdate deep-compared the whole props object, so this cannot
    // become a dependency array. It runs after every render and skips the first,
    // which is what componentDidUpdate did.
    const prevProps = useRef<TObjectListProps | null>(null);
    useEffect(() => {
        const previous = prevProps.current;
        prevProps.current = props;
        if (previous === null) {
            return;
        }
        if (!_.isEqual(previous, props)) {
            load(props);
        }
        if (!props.searchObject) {
            return;
        }
        const type = props.searchObject.type;
        if (previous.searchObject.type !== type) {
            if (selectedColumns[type]) {
                const newSearch = props.searchObject.setDefaultColumns(selectedColumns[type]);
                props.updateSearchObject(newSearch);
            }
        }
    });

    const onTableColumnsChange = (columns) => {
        const searchObject = props.searchObject.setDefaultColumns(columns);
        props.updateSearchObject(searchObject);
        const allColumns = _.clone(selectedColumns);
        allColumns[searchObject.type] = searchObject.defaultColumns;
        API.setPreferencesWithRefs(
            "multiObjectSelectColumns",
            [],
            [JSON.stringify(allColumns)],
            _.noop
        );
        setSelectedColumns(allColumns);
    };

    const isRowLoaded = (index) => objects[index] !== undefined;

    // eslint-disable-next-line no-unused-vars
    const loadMoreRows = ({ startIndex, stopIndex }) => {
        load(props);
    };

    const getTableRow = ({ index }) => {
        const object = objects[index];
        const result = {};
        props.searchObject.defaultColumns.forEach((column) => {
            if (!object) {
                result[column.name] = "-";
            } else {
                const values = _.find(object.fields, (field) => field.id === column.id);
                if (!values || column.id === "_admin_color_") {
                    if (column.id === "type") {
                        result[column.name] = getTypeName(
                            object.typeId,
                            props.options,
                            TimeEdit.rootType
                        );
                    } else if (column.id === "_admin_color_") {
                        result[column.name] = object.color || "-";
                    } else if (
                        column.id === getMembershipColumn().id &&
                        props.presentMembershipPeriod
                    ) {
                        result[column.name] = props.presentMembershipPeriod(object.id);
                    } else if (column.id === "placeholder") {
                        let value = object.fields[0].values.join(", ");
                        if (!value) {
                            value = object.name || object.extid || object.id;
                        }
                        result[column.name] = value;
                    } else {
                        result[column.name] = "";
                    }
                } else {
                    result[column.name] = values.values ? values.values.join(", ") : "";
                }
            }
        });
        return result;
    };

    let count: string | null = null;
    if (props.totalNumber) {
        count = `(${objects.length}/${props.totalNumber})`;
    }
    const classNames = ["selectBox"];
    if (props.cssClass) {
        classNames.push(props.cssClass);
    }
    if (props.searchObject) {
        const allColumns = (props.searchObject.columns || []).concat(getMembershipColumn());
        const allColIds = allColumns.map((column) => column.id);
        const columns = (props.searchObject.defaultColumns || []).filter(
            (column) => allColIds.indexOf(column.id) !== -1
        );
        const flexStyle = props.flex ? { display: "flex", flex: "1 1 auto" } : {};
        return (
            <div style={flexStyle}>
                {" "}
                {count}
                <div
                    className={classNames.join(" ")}
                    style={{
                        display: "flex",
                        flexGrow: "1",
                        flexDirection: "column",
                        height: props.flex ? undefined : "200px",
                        overflow: "hidden",
                    }}
                >
                    <Table
                        language={Language}
                        log={Log}
                        columns={columns}
                        cellRenderers={props.cellRenderers || undefined}
                        allColumns={props.hidePlusButton ? undefined : allColumns}
                        columnWidths={[]}
                        isRowLoaded={isRowLoaded}
                        loadMoreRows={loadMoreRows}
                        presentModal={appContext.presentModal}
                        contextMenu={ContextMenu}
                        rowCount={objects.length}
                        onSelect={(index, rowData, event) =>
                            props.onChange(index, rowData, event, objects[index])
                        }
                        getTableRow={getTableRow}
                        onColumnChange={onTableColumnsChange}
                        maxColumns={10}
                        hoverButton={props.hoverButton}
                        highlight={
                            props.allowMultiSelection
                                ? Table.HIGHLIGHT.MULTI
                                : Table.HIGHLIGHT.SINGLE
                        }
                    />
                </div>
            </div>
        );
    }
    return (
        <div className={classNames.join(" ")}>
            <div className="columnTitles columnHeader">
                <span>
                    {props.columnTitle} {count}
                </span>
            </div>
            <select size={props.listSize} onChange={props.onChange}>
                {objects.map((object) => {
                    const name = object.name || object.extid || object.id;
                    return (
                        <option key={object.id} value={object.id}>
                            {name}
                        </option>
                    );
                })}
            </select>
        </div>
    );
};

export default ObjectList;
